/*=============================================== InputPage ===============================================*/

import { useState, useContext } from "react"
import type { ChangeEvent } from "react"
import { Link } from "react-router-dom"
import { Input, Text, CodeContainer, Grid } from "tsx-library-julseb"
import type { ValidationTypes } from "tsx-library-julseb/types"

import { LangContext } from "context"
import type { LangContextType } from "context"

import { ComponentsLayout } from "components"
import type { ItemType, PropsItemType } from "components"

import {
    commonProps,
    iconBoxicon,
    inputLabel,
    inputBackgroundColor,
    inputVariants,
    validationObject,
    inputIcon,
    inputCounter,
} from "data"

const InputPage = () => {
    const { language } = useContext(LangContext) as LangContextType

    const [inputs, setInputs] = useState({
        text: "",
        textDisabled: "",
        textValidate: "",
        textIcon: "",
        textHelpers: "",
        textCounter: "",
        password: "Password",
        passwordIcon: "Password",
        file: "",
        fileDisabled: "",
        url: "",
        calendar: "",
        calendarLocal: "",
        time: "",
        search: "",
        searchFilled: "Search",
        select: "option",
        textarea: "",
        textareaDisabled: "",
    })

    const handleInputs = (
        e: ChangeEvent<
            HTMLInputElement & HTMLTextAreaElement & HTMLSelectElement
        >
    ) =>
        setInputs({
            ...inputs,
            [e.target.id]: e.target.value,
        })

    const [validation, setValidation] = useState<ValidationTypes>(undefined)
    const [inputValidate, setInputValidate] = useState("")
    const handleValidate = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValidate(e.target.value)

        if (e.target.value.length > 0) {
            setValidation(e.target.value.length >= 3 ? "passed" : "not-passed")
        } else {
            setValidation(undefined)
        }
    }

    const clearSearch = () =>
        setInputs({
            ...inputs,
            search: "",
        })

    const clearSearchFilled = () =>
        setInputs({
            ...inputs,
            searchFilled: "",
        })

    const items: ItemType[] = [
        {
            title: "Text inputs",
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="text"
                        placeholder="Input text"
                        value={inputs.text}
                        onChange={handleInputs}
                    />

                    <Input
                        id="textDisabled"
                        placeholder="Input disabled"
                        value={inputs.textDisabled}
                        onChange={handleInputs}
                        disabled
                    />

                    <Input
                        id="textValidate"
                        placeholder="Input with validation"
                        value={inputs.textValidate}
                        onChange={handleInputs}
                        validation="not-passed"
                    />

                    <Input
                        id="textIcon"
                        placeholder="Input with icon"
                        value={inputs.textIcon}
                        onChange={handleInputs}
                        icon="mail"
                    />
                </Grid>
            ),
            code: '<Input id="text" placeholder="Input text" value={inputs.text} onChange={handleInputs} />\n<Input id="textDisabled" placeholder="Input disabled" value={inputs.textDisabled} onChange={handleInputs} disabled />\n<Input id="textValidate" placeholder="Input with validation" value={inputs.textValidate} onChange={handleInputs} validation="not-passed" />\n<Input id="textIcon" placeholder="Input with icon" value={inputs.textIcon} onChange={handleInputs} icon="mail" />',
        },
        {
            title: "With label and helpers",
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="textHelpers"
                        label="Label"
                        helper="Helper"
                        helperBottom="Helper bottom"
                        value={inputs.textHelpers}
                        onChange={handleInputs}
                    />
                </Grid>
            ),
            code: '<Input id="textHelpers" label="Label" helper="Helper" helperBottom="Helper bottom" value={inputs.textHelpers} onChange={handleInputs} />',
        },
        {
            title: "With validation",
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="inputValidation"
                        label="Input validation"
                        validation={validation}
                        helperBottom={{
                            text:
                                validation === "not-passed"
                                    ? "Minimum 3 characters"
                                    : "",
                            icon:
                                validation === "not-passed"
                                    ? "close-circle"
                                    : undefined,
                            iconColor: "danger",
                        }}
                        value={inputValidate}
                        onChange={handleValidate}
                        variant="pill"
                    />
                </Grid>
            ),
            code: 'const [validation, setValidation] = useState(undefined)\nconst [inputValidate, setInputValidate] = useState("")\nconst handleValidate = e => {\n    setInputValidate(e.target.value)\n\n    if (e.target.value.length > 0) {\n        setValidation(e.target.value.length >= 3 ? "passed" : "not-passed")\n    } else {\n        setValidation(undefined)\n    }\n}\n\n<Input\n    id="inputValidation"\n    label="Input validation"\n    validation={validation}\n    helperBottom={{\n        text: validation === "not-passed" ? "Minimum 3 characters" : "",\n        icon: validation === "not-passed" ? "close-circle" : undefined,\n        iconColor: "danger",\n    }}\n    value={inputValidate}\n    onChange={handleValidate}\n    variant="pill"\n/>',
            codeTs: 'const [validation, setValidation] = useState<ValidationTypes>(undefined)\nconst [inputValidate, setInputValidate] = useState<string>("")\nconst handleValidate = (e: ChangeEvent<HTMLInputElement>) => {\n    setInputValidate(e.target.value)\n\n    if (e.target.value.length > 0) {\n        setValidation(e.target.value.length >= 3 ? "passed" : "not-passed")\n    } else {\n        setValidation(undefined)\n    }\n}\n\n<Input\n    id="inputValidation"\n    label="Input validation"\n    validation={validation}\n    helperBottom={{\n        text: validation === "not-passed" ? "Minimum 3 characters" : "",\n        icon: validation === "not-passed" ? "close-circle" : undefined,\n        iconColor: "danger",\n    }}\n    value={inputValidate}\n    onChange={handleValidate}\n    variant="pill"\n/>',
        },
        {
            title: "Input with counter",
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="textCounter"
                        placeholder="Input with counter"
                        value={inputs.textCounter}
                        onChange={handleInputs}
                        counter
                        maxLength={140}
                    />
                </Grid>
            ),
            code: '<Input id="textCounter" placeholder="Input with counter" value={inputs.textCounter} onChange={handleInputs} counter maxLength={140} />',
        },
        {
            title: "Password",
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="password"
                        placeholder="Password"
                        value={inputs.password}
                        onChange={handleInputs}
                        password
                    />

                    <Input
                        id="passwordIcon"
                        placeholder="Password with icon"
                        value={inputs.passwordIcon}
                        onChange={handleInputs}
                        password
                        button={{
                            iconShow: "hide",
                            iconHide: "show",
                        }}
                    />
                </Grid>
            ),
            code: '<Input\n    id="password"\n    placeholder="Password"\n    value={inputs.password}\n    onChange={handleInputs}\n    password\n/>\n\n<Input\n    id="passwordIcon"\n    placeholder="Password with icon"\n    value={inputs.passwordIcon}\n    onChange={handleInputs}\n    password\n    button={{\n        iconShow: "hide",\n        iconHide: "show",\n    }}\n/>',
        },
        {
            title: "File input",
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="file"
                        type="file"
                        value={inputs.file}
                        onChange={handleInputs}
                    />

                    <Input
                        id="fileDisabled"
                        type="file"
                        value={inputs.fileDisabled}
                        onChange={handleInputs}
                        disabled
                    />
                </Grid>
            ),
            code: '<Input id="file" type="file" value={inputs.file} onChange={handleInputs} />\n<Input id="fileDisabled" type="file" value={inputs.fileDisabled} onChange={handleInputs} disabled />',
        },
        {
            title: "Calendar input",
            intro: (
                <>
                    Here we are using the native browser input. For a styled
                    input, check{" "}
                    <Link to="/components/datepicker">
                        Datepicker component
                    </Link>
                    .
                </>
            ),
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="calendar"
                        type="date"
                        label="Date"
                        value={inputs.calendar}
                        onChange={handleInputs}
                    />

                    <Input
                        id="calendarLocal"
                        type="datetime-local"
                        label="Date local"
                        value={inputs.calendarLocal}
                        onChange={handleInputs}
                    />
                </Grid>
            ),
            code: '<Input\n    id="calendar"\n    type="date"\n    label="Date"\n    value={inputs.calendar}\n    onChange={handleInputs}\n/>\n\n<Input\n    id="calendarLocal"\n    type="datetime-local"\n    label="Date local"\n    value={inputs.calendarLocal}\n    onChange={handleInputs}\n/>',
        },
        {
            title: "Time input",
            intro: (
                <>
                    Here we are using the native browser input. For a styled
                    input, check{" "}
                    <Link to="/components/timepicker">
                        Timepicker component
                    </Link>
                    .
                </>
            ),
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="time"
                        type="time"
                        label="Time input"
                        value={inputs.time}
                        onChange={handleInputs}
                    />
                </Grid>
            ),
            code: '<Input id="time" type="time" label="Time input" value={inputs.time} onChange={handleInputs} />',
        },
        {
            title: "Search input",
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="search"
                        type="search"
                        value={inputs.search}
                        onChange={handleInputs}
                        clearSearch={clearSearch}
                    />

                    <Input
                        id="searchFilled"
                        type="search"
                        value={inputs.searchFilled}
                        onChange={handleInputs}
                        clearSearch={clearSearchFilled}
                        focusKeys={["Command", "KeyJ"]}
                        showKeys
                    />
                </Grid>
            ),
            code: 'const clearSearch = () =>\n    setInputs({\n        ...inputs,\n        search: "",\n    })\n\nconst clearSearchFilled = () =>\n    setInputs({\n        ...inputs,\n        searchFilled: "",\n    })\n\n<Input id="search" type="search" value={inputs.search} onChange={handleInputs} clearSearch={clearSearch} />\n<Input id="searchFilled" type="search" value={inputs.searchFilled} onChange={handleInputs} clearSearch={clearSearchFilled} focusKeys={["Command", "KeyJ"]} showKeys />',
        },
        {
            title: "Select",
            intro: (
                <>
                    Here we are using the native browser input. For a styled
                    input, check{" "}
                    <Link to="/components/select">Select component</Link>.
                </>
            ),
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="select"
                        type="select"
                        value={inputs.select}
                        onChange={handleInputs}
                    >
                        <option value="option">option</option>
                    </Input>
                </Grid>
            ),
            code: '<Input id="select" type="select" value={inputs.select} onChange={handleInputs}>\n    <option value="option">option</option>\n</Input>',
        },
        {
            title: "Text area",
            content: (
                <Grid col={2} gap="s">
                    <Input
                        id="textarea"
                        type="textarea"
                        value={inputs.textarea}
                        onChange={handleInputs}
                        placeholder="Textarea"
                    />

                    <Input
                        id="textareaDisabled"
                        type="textarea"
                        value={inputs.textareaDisabled}
                        onChange={handleInputs}
                        disabled
                        placeholder="Textarea disabled"
                    />
                </Grid>
            ),
            code: '<Input id="textarea" type="textarea" value={inputs.textarea} onChange={handleInputs} placeholder="Textarea" />\n<Input id="textareaDisabled" type="textarea" value={inputs.textareaDisabled} onChange={handleInputs} disabled placeholder="Textarea disabled" />',
        },
    ]

    const propsItems: PropsItemType[] = [
        inputVariants(),
        inputBackgroundColor(),
        ...inputLabel("small"),
        "separator",
        {
            title: "Text inputs",
            subtitle: "type",
            intro: "This is the default if you do net set any type.",
            type: "string",
            possible: ["email", "number", "password", "tel", "text", "url"],
            default: "text",
        },
        ...inputIcon(false),
        ...inputCounter,
        ...validationObject("small"),
        "separator",
        {
            title: "Input password",
            subtitle: "password",
            type: "boolean",
        },
        ...inputIcon(),
        ...inputCounter,
        ...validationObject("small"),
        "separator-small",
        {
            subtitle: "button",
            type: "object",
        },
        {
            title: (
                <>
                    <code>button</code> object
                </>
            ),
            subtitle: "textShow",
            type: "string",
            default: "Show",
        },
        {
            subtitle: "textHide",
            type: "string",
            default: "Hide",
        },
        {
            subtitle: "iconShow",
            intro: commonProps.addIconCapital,
            type: "string",
        },
        {
            subtitle: "iconHide",
            type: "string",
        },
        "separator",
        {
            title: "Search input",
            subtitle: "type",
            type: "string",
            possible: ["search"],
        },
        {
            subtitle: "clearSearch",
            type: "function",
            required: true,
        },
        {
            subtitle: "focusKeys",
            type: "array of strings",
        },
        {
            subtitle: "showKeys",
            intro: (
                <>
                    Only if <code>focusKeys</code> is defined.
                </>
            ),
            type: "boolean",
        },
        ...inputIcon(),
        {
            subtitle: "iconClear",
            type: "string",
            default: iconBoxicon("x"),
        },
        "separator",
        {
            title: "Date input",
            subtitle: "type",
            intro: (
                <>
                    Here we are using the native browser inputs. For a styled
                    input, check{" "}
                    <Link to="/components/datepicker">Datepicker</Link>{" "}
                    component.
                </>
            ),
            type: "string",
            possible: ["date", "datetime-local", "month", "week"],
        },
        ...inputIcon(),
        {
            subtitle: "iconCalendar",
            type: "string",
            default: iconBoxicon("calendar"),
        },
        ...validationObject("small"),
        "separator",
        {
            title: "Time input",
            subtitle: "type",
            type: "string",
            possible: ["time"],
            intro: (
                <>
                    Here we are using the native browser inputs. For a styled
                    input, check{" "}
                    <Link to="/components/timepicker">Timepicker</Link>{" "}
                    component.
                </>
            ),
        },
        ...inputIcon(),
        {
            subtitle: "iconClock",
            type: "string",
            default: iconBoxicon("time"),
        },
        ...validationObject("small"),
        "separator",
        {
            title: "File input",
            subtitle: "type",
            type: "string",
            possible: ["file"],
        },
        ...validationObject("small"),
        "separator",
        {
            title: "Color input",
            subtitle: "type",
            type: "string",
            possible: ["color"],
        },
        "separator",
        {
            title: "Select input",
            subtitle: "type",
            type: "string",
            possible: ["select"],
        },
        {
            subtitle: "iconSelect",
            type: "string",
            default: iconBoxicon("caret-down"),
        },
        "separator",
        {
            title: "Textarea input",
            subtitle: "type",
            type: "string",
            possible: ["textarea"],
        },
        ...inputCounter,
        ...validationObject("small"),
    ]

    return (
        <ComponentsLayout items={items} propsItems={propsItems}>
            <Text>For most inputs, we are using these functions:</Text>

            {language === "ts" ? (
                <CodeContainer language="typescript">
                    {
                        'const [inputs, setInputs] = useState({\n    text: "",\n    textDisabled: "",\n    textValidate: "",\n    textIcon: "",\n    textHelpers: "",\n    textCounter: "",\n    password: "Password",\n    passwordIcon: "Password",\n    file: "",\n    fileDisabled: "",\n    url: "",\n    calendar: "",\n    calendarLocal: "",\n    time: "",\n    search: "",\n    searchFilled: "Search",\n    select: "option",\n    textarea: "",\n    textareaDisabled: "",\n})\n\nconst handleInputs = (e: ChangeEvent<HTMLInputElement & HTMLTextAreaElement & HTMLSelectElement>) =>\n    setInputs({\n        ...inputs,\n        [e.target.id]: e.target.value,\n    })'
                    }
                </CodeContainer>
            ) : (
                <CodeContainer language="javascript">
                    {
                        'const [inputs, setInputs] = useState({\n    text: "",\n    textDisabled: "",\n    textValidate: "",\n    textIcon: "",\n    textHelpers: "",\n    textCounter: "",\n    password: "Password",\n    passwordIcon: "Password",\n    file: "",\n    fileDisabled: "",\n    url: "",\n    calendar: "",\n    calendarLocal: "",\n    time: "",\n    search: "",\n    searchFilled: "Search",\n    select: "option",\n    textarea: "",\n    textareaDisabled: "",\n})\n\nconst handleInputs = e =>\n    setInputs({\n        ...inputs,\n        [e.target.id]: e.target.value,\n    })'
                    }
                </CodeContainer>
            )}
        </ComponentsLayout>
    )
}

export default InputPage
